import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { useBulkCreateCampaignIntegrationInvoices } from "../hooks/useBulkCreateCampaignIntegrationInvoices";
import { useBulkUpdateCampaignIntegrationInvoices } from "../hooks/useBulkUpdateCampaignIntegrationInvoices";
import { useDownloadInvoiceCsv } from "../hooks/useDownloadInvoiceCsv";
import { useIntegrationModuleCampaigns } from "../hooks/useIntegrationModuleCampaigns";
import { InvoiceIntegrationModuleTableToolbar } from "./components/InvoiceIntegrationModuleTableToolbar";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import {
  EditableDataTable,
  EditableDataTableRef,
} from "@/components/ui/v2/table/editable-data-table";
import Tabs from "@/components/ui/v2/tabs";
import { ROUTES } from "@/constants/routes";
import { translateIntegrationDisplayName } from "@/features/integrations/utils";
import { useIntegrationModuleForInvoices } from "@/features/invoices/hooks/useIntegrationModuleForInvoices";
import { parseAmount } from "@/lib/helpers/common";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { useDateConstraints } from "@/lib/hooks/useDateConstraints";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import { useCampaignIntegrationMonthTableColumns } from "@/lib/tableColumns/useCampaignIntegrationMonthTableColumns";
import { isYearMonthInFuture } from "@/lib/utils/dateUtils";
import { CampaignIntegrationMonthType } from "@/types/response/CampaignIntegrationMonthType";
import { SortDirection, TableColumn } from "@/types/table/tableProps";

type InvoiceIntegrationModulePageContentProps = {
  currentPageLabel?: string;
  descending: boolean | undefined;
  handleSort: (
    accessor: TableColumn<CampaignIntegrationMonthType>["accessor"],
    direction: SortDirection | null,
  ) => void;
  tenantIntegrationModuleId: string;
  month: string;
  orderBy?: string;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  year: string;
  tenantId: string | null;
};

const InvoiceIntegrationModulePageContent = ({
  currentPageLabel,
  descending,
  handleSort,
  tenantIntegrationModuleId,
  month,
  orderBy,
  page,
  pageSize,
  setPage,
  setPageSize,
  year,
  tenantId,
}: InvoiceIntegrationModulePageContentProps) => {
  const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();
  const bulkCreateInvoice = useBulkCreateCampaignIntegrationInvoices({
    tenantIntegrationModuleId,
    year,
    month,
    tenantId,
  });
  const bulkUpdateInvoice = useBulkUpdateCampaignIntegrationInvoices({
    tenantIntegrationModuleId,
    year,
    month,
    tenantId,
  });
  const location = useLocation();
  const getSelectedTabId = () => {
    const currentPath = location.pathname;
    if (currentPath.includes(`/${ROUTES.toDo}`)) {
      return "toDo";
    }
    if (currentPath.includes(`/${ROUTES.invoiced}`)) {
      return "invoiced";
    }
    return "toDo";
  };
  const selectedTabId = getSelectedTabId();
  const isInvoiced = selectedTabId === "invoiced";
  const {
    data: campaignIntegrationMonths,
    isLoading,
    isFetching,
  } = useIntegrationModuleCampaigns({
    tenantIntegrationModuleId,
    year,
    month,
    descending,
    orderBy,
    page,
    pageSize,
    search: debouncedSearchValue,
    tenantId,
    isInvoiced,
  });
  const { campaignIntegrationMonthTableColumns } =
    useCampaignIntegrationMonthTableColumns();
  const { t, i18n } = useTranslation();
  const { data: integrationModule } = useIntegrationModuleForInvoices({
    tenantIntegrationModuleId,
    tenantId,
  });
  const { maxDate } = useDateConstraints();
  const [isEditMode, setIsEditMode] = useState(false);
  const editableTableRef =
    useRef<EditableDataTableRef<CampaignIntegrationMonthType>>(null);

  useEffect(() => {
    if (isEditMode) {
      setIsEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearchValue, year, month]);

  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      campaignIntegrationMonths?.data &&
      campaignIntegrationMonths.data.length === 0 &&
      page > 1 &&
      campaignIntegrationMonths.totalRecords !== undefined
    ) {
      const totalPages = Math.ceil(
        campaignIntegrationMonths.totalRecords / pageSize,
      );
      const targetPage = Math.max(1, totalPages);
      if (targetPage !== page) {
        setPage(targetPage);
      }
    }
  }, [
    campaignIntegrationMonths?.data,
    campaignIntegrationMonths?.totalRecords,
    isLoading,
    isFetching,
    page,
    pageSize,
    setPage,
  ]);

  const handleUpdateData = async (
    newData: CampaignIntegrationMonthType[],
  ) => {
    const invoices = newData
      .filter((item) => item.spent !== null && item.spent !== undefined)
      .map((item) => ({
        campaignId: item.campaignId,
        clientIntegrationModuleId: item.clientIntegrationModuleId,
        clientId2: item.clientId2,
        amount: parseAmount(item.spent),
      }));
    if (invoices.length === 0) return;

    const mutation = isInvoiced ? bulkUpdateInvoice : bulkCreateInvoice;
    await mutation.mutateAsync({ invoices });
  };

  const handleSaveChanges = async () => {
    const editedData = editableTableRef.current?.getEditedData() ?? [];
    await handleUpdateData(editedData);
    setIsEditMode(false);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleEditModeChange = (newEditMode: boolean) => {
    setIsEditMode(newEditMode);
  };

  const downloadMutation = useDownloadInvoiceCsv();

  const handleDownloadCsv = () => {
    downloadMutation.mutate({
      apiEndpointBase: "Integrations",
      entityId: tenantIntegrationModuleId,
      year,
      month,
      tenantId,
      urlPath: `/api/v2/Integrations/${tenantIntegrationModuleId}/Campaigns/${year}/${month}/Download`,
      extraParams: { isInvoiced },
    });
  };

  const tabs = [
    {
      id: "toDo",
      label: t("common.toDo"),
      href: `/${ROUTES.invoices}/${ROUTES.integrations}/${tenantIntegrationModuleId}/${ROUTES.toDo}/${year}/${month}`,
      content: <></>,
    },
    {
      id: "invoiced",
      label: t("common.invoiced"),
      href: `/${ROUTES.invoices}/${ROUTES.integrations}/${tenantIntegrationModuleId}/${ROUTES.invoiced}/${year}/${month}`,
      content: <></>,
    },
  ];

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.invoices"), href: `/${ROUTES.invoices}` },
      {
        label: t("common.jobBoards"),
        href: `/${ROUTES.invoices}/${ROUTES.integrations}`,
      },
      {
        label: translateIntegrationDisplayName(currentPageLabel ?? integrationModule?.name, t, i18n) || t("common.integration"),
      },
    ],
  });

  return (
    <ModernPageLayout
      backbutton={{
        href: `../../`,
        label: t("common.back"),
      }}
      breadcrumbs={breadcrumbs}
      title={`${t("common.invoices")} ${t("common.for")} ${
        translateIntegrationDisplayName(currentPageLabel ?? integrationModule?.name, t, i18n) ?? "..."
      }`}
    >
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between w-full">
          <Tabs tabs={tabs} defaultTabId={getSelectedTabId()} />
        </div>

        <InvoiceIntegrationModuleTableToolbar
          onSearchChange={setSearchValue}
          onDownload={handleDownloadCsv}
          isDownloading={downloadMutation.isPending}
          searchValue={searchValue}
          year={year}
          month={month}
          tenantIntegrationModuleId={tenantIntegrationModuleId}
          maxDate={maxDate}
          currentPageLabel={currentPageLabel}
          integrationModuleName={translateIntegrationDisplayName(currentPageLabel ?? integrationModule?.name, t, i18n) || integrationModule?.name}
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          isEditDisabled={isYearMonthInFuture([year, month])}
          isSaving={
            (isInvoiced ? bulkUpdateInvoice : bulkCreateInvoice).isPending
          }
          hasData={(campaignIntegrationMonths?.data?.length ?? 0) > 0}
        />

        <EditableDataTable<CampaignIntegrationMonthType>
          ref={editableTableRef}
          columns={campaignIntegrationMonthTableColumns}
          data={campaignIntegrationMonths?.data ?? []}
          descending={descending}
          editableFields={["spent"]}
          entityName={"Invoice"}
          getRowId={(row) => `${row.campaignId}-${row.clientId2}`}
          isEditDisabled={isYearMonthInFuture([year, month])}
          isEditing={
            (isInvoiced ? bulkUpdateInvoice : bulkCreateInvoice).isPending
          }
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          isLoading={isLoading || isFetching}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          onUpdateData={handleUpdateData}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          orderBy={orderBy}
          page={page}
          pageSize={pageSize}
          totalRecords={campaignIntegrationMonths?.totalRecords ?? 0}
        />
      </div>
    </ModernPageLayout>
  );
};

const InvoiceIntegrationModulePage = () => {
  const { tenantIntegrationModuleId, year, month } = useParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler<CampaignIntegrationMonthType>();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { tenantId } = useAuth();
  const location = useLocation();

  if (!tenantIntegrationModuleId || !month || !year) return null;

  return (
    <InvoiceIntegrationModulePageContent
      currentPageLabel={location.state?.integrationModuleName}
      descending={descending}
      handleSort={handleSort}
      tenantIntegrationModuleId={tenantIntegrationModuleId}
      month={month}
      orderBy={orderBy}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      year={year}
      tenantId={tenantId}
    />
  );
};

export default InvoiceIntegrationModulePage;
